<template>
  <ValidationObserver ref="quoteSlipForm">
    <div class="card card-custom">
      <div class="card-body container">

        <div class="card-body p-0">
          <!--begin: Wizard-->
          <div id="quoteSlipFormWizard" class="wizard wizard-3" data-wizard-state="step-first">
            <!--begin: Wizard Body-->
            <div class="card card-custom card-shadowless rounded-top-0">
              <div class="card-body p-0">
                <div class="row justify-content-center py-8 px-8 py-lg-10 px-lg-10">
                  <div class="col-xl-12 col-xxl-12">
                    <!--begin: Wizard Form-->
                    <div class="form mt-0" id="kt_form">

                      <!--begin: ERROR ALERT-->
                      <b-alert v-if="errorAlert" variant="warning" show>
                        <div class="text-dark" v-html="errorAlert"></div>
                      </b-alert>
                      <!--end: ERROR ALERT-->

                      <div>
                        <div class="form-group row mt-10 mb-0">
                          <div class="col-2">
                            <label class="col-form-label text-left">
                              Select Brand<span class="text-danger">*</span>
                            </label>
                            <ValidationProvider rules="required" name="brand" v-slot="{ errors }">
                              <b-form-select class="custom-select custom-select-md" v-model="brand" :disabled="!!extractedData">
                                <option value="Fuse">Fuse</option>
                                <option value="CRI">CRI</option>
                                <option value="Rentsure">Rentsure</option>
                              </b-form-select>
                              <span class="text-danger">{{ errors[0] }}</span>
                            </ValidationProvider>
                          </div>
                        </div>

                        <div class="form-group row mb-3">
                          <div class="col-6">
                            <label class="col-form-label text-left">
                              Select Input Files<span class="text-danger">*</span>
                            </label>
                            <ValidationProvider rules="required" name="selectedFiles" v-slot="{ errors }">
                              <b-form-file class="form-control" placeholder="Drop files here, or click here to select files"
                                           v-model="selectedFiles"  multiple :disabled="!!extractedData">
                                <template slot="file-name" slot-scope="{ names }">
                                  <b-badge variant="secondary">{{ names[0] }}</b-badge>
                                  <b-badge v-if="names.length > 1" variant="secondary" class="ml-1">
                                    + {{ names.length - 1 }} More files
                                  </b-badge>
                                </template>
                              </b-form-file>
                              <span class="text-danger">{{ errors[0] }}</span>
                            </ValidationProvider>
                          </div>
                        </div>
                        <div class="form-group">
                          <button class="btn btn-primary font-weight-bold" @click.prevent="uploadFiles" :disabled="jobProcessing || !!extractedData">
                            Upload
                          </button>
                        </div>

                        <div v-if="jobProcessing">
                          <div class="form-group row mb-0">
                            <div class="col-12">
                              <label class="col-form-label text-left font-weight-bold font-size-h6">
                                <b-spinner class="align-middle" variant="primary" label="Processing..."></b-spinner>
                                Processing files...
                                <span v-if="jobPercent" class="font-weight-boldest">{{jobPercent}} % completed.</span>
                                <span v-else>please wait.</span>
                              </label>
                            </div>
                          </div>
                        </div>

                        <div v-if="extractedData">
                          <div class="form-group row mb-0">
                            <div class="col-12">
                              <label class="col-form-label text-left font-weight-bold font-size-h6">
                                <i class="fa fa-check-circle text-success font-weight-boldest"></i>
                                Data extracted successfully from uploaded files for brand <strong>{{ brand }}</strong>.
                              </label>
                            </div>
                          </div>
                          <div class="form-group row">
                            <div class="col-4">
                              <label class="col-form-label text-left">
                                Contact First Name<span class="text-danger">*</span>
                              </label>
                              <ValidationProvider rules="required|max:255" name="firstName" v-slot="{ errors }">
                                <input type="text" class="form-control" v-model.trim="firstName" maxlength="255"/>
                                <span class="text-danger">{{ errors[0] }}</span>
                              </ValidationProvider>
                            </div>
                            <div class="col-4">
                              <label class="col-form-label text-left">
                                Contact Last Name<span class="text-danger">*</span>
                              </label>
                              <ValidationProvider rules="required|max:255" name="lastName" v-slot="{ errors }">
                                <input type="text" class="form-control" v-model.trim="lastName" maxlength="255"/>
                                <span class="text-danger">{{ errors[0] }}</span>
                              </ValidationProvider>
                            </div>
                            <div class="col-4">
                              <label class="col-form-label text-left">
                                Contact Email<span class="text-danger">*</span>
                              </label>
                              <ValidationProvider rules="required|email|max:100" name="email" v-slot="{ errors }">
                                <input type="text" class="form-control" v-model.trim="email" maxlength="100"/>
                                <span class="text-danger">{{ errors[0] }}</span>
                              </ValidationProvider>
                            </div>
                          </div>

                          <span class="font-weight-boldest font-size-h5">Proposed Insured Entity</span>
                          <div class="form-group row">
                            <label class="col-1 col-form-label">Search ABN<span class="text-danger">*</span></label>
                            <span class="col-3">
                              <ValidationProvider :rules="{ regex: /^[\d\s]+$/ }" name="abnSearchString" v-slot="{ errors }">
                                <input type="text" class="form-control" v-model.trim="abnSearchString" maxlength="15"/>
                                <span class="text-danger">{{ errors[0] }}</span>
                              </ValidationProvider>
                            </span>
                            <button type="button" class="btn btn-primary" @click.prevent="searchAbn"
                                    :disabled="!abnSearchString || searchingAbn">Search</button>
                            <b-spinner v-if="searchingAbn" variant="primary" class="ml-3 mt-1"></b-spinner>
                          </div>
                          <div class="form-group" v-if="abnSearchError">
                            <label class="text-danger">{{ abnSearchError }}</label>
                          </div>

                          <!-- Validation to make sure the ABN entity is selected -->
                          <ValidationProvider rules="required" name="abnEntity" v-slot="{ errors }">
                            <input type="hidden" v-model="abnEntity"/>
                            <span class="text-danger">{{ errors[0] ? 'Selecting an ABN entity is mandatory.' : '' }}</span>
                          </ValidationProvider>

                          <div v-if="abnEntity && abnEntity.entityName && abnEntity.abn" class="mb-5 mt-5">
                            <div class="form-group row mb-0">
                              <div class="col-1 font-size-h6">Entity Name</div>
                              <div class="col-5 font-weight-boldest font-size-h6">{{ abnEntity.entityName }}</div>
                            </div>
                            <div class="form-group row mb-0">
                              <div class="col-1 font-size-h6">ABN</div>
                              <div class="col-5">{{ abnEntity.abn }}</div>
                            </div>
                            <div v-if="abnEntity.acn" class="form-group row mb-0">
                              <div class="col-1 font-size-h6">ACN</div>
                              <div class="col-5">{{ abnEntity.acn }}</div>
                            </div>

                            <ValidationProvider :rules="{'required-action': {allowFalse:false}}" name="abnEntityConfirmed" v-slot="{ errors }">
                              <div class="row mt-2">
                                <span class="switch switch-sm switch-outline switch-icon switch-primary ml-3">
                                  <label>
                                    <input type="checkbox" name="select" v-model="abnEntityConfirmed"/>
                                    <span></span>
                                  </label>
                                </span>
                                <label class="col-form-label ml-2 font-size-h6">
                                  I confirm that the proposed insured entity displayed above is correct.
                                </label>
                              </div>
                              <span class="text-danger">{{ errors[0] }}</span>
                            </ValidationProvider>
                          </div>

                          <div class="d-flex justify-content-between border-top pt-8">
                            <div>
                              <button class="btn btn-primary font-weight-bold" @click.prevent="createProposalForm">
                                Create Proposal Form
                              </button>
                              <button class="btn btn-outline-primary font-weight-bold ml-5" @click.prevent="showViewExtractedDataModal">
                                View Extracted Data
                              </button>
                              <button class="btn btn-secondary font-weight-bold ml-5" @click.prevent="resetAll">
                                Reset
                              </button>
                            </div>
                          </div>

                          <b-modal ref="viewDataModal" title="Extracted Data" size="lg" no-close-on-backdrop>
                            <div class="form-group" v-if="extractedData">
                              <pre>{{ JSON.stringify(extractedData, null, 2) }}</pre>
                            </div>
                          </b-modal>
                        </div>
                      </div>
                    </div>
                    <!--end: Wizard Form-->
                  </div>
                </div>
              </div>
            </div>
            <!--end: Wizard Body-->
          </div>
          <!--end: Wizard-->
        </div>
      </div>
    </div>
  </ValidationObserver>
</template>

<style lang="scss">
@import "@/assets/sass/pages/wizard/wizard-3.scss";

.page-loading {
  display: flex;
  justify-content: center;
  align-items: center;
}

.step-disabled {
  cursor: not-allowed !important;
}

body.swal2-shown > [aria-hidden="true"] {
  transition: 0.1s filter;
  filter: blur(10px);
}

</style>

<script>
import KTWizard from "@/assets/js/components/wizard";
import _ from "lodash";
import KTUtil from "@/assets/js/components/util";
import {ADD_BODY_CLASSNAME, REMOVE_BODY_CLASSNAME} from "@/core/services/store/htmlclass.module";
import ApiService from "@/core/services/api.service";
import FormData from "form-data";

export default {
  name: "quoteSlipForm",

  data() {
    return {
      wizardObj: null,
      errorAlert: null,
      brand: 'Fuse', // Default to Fuse
      firstName: null,
      lastName: null,
      email: null,
      selectedFiles: null,
      abnEntity: null,
      abnEntityConfirmed: false,

      abnSearchString: null,
      abnSearchError: null,
      searchingAbn: false,

      refreshInterval: 10000,
      jobId: null,
      jobStatus: null,
      jobPercent: null,
      jobProcessing: false,
      extractedData: null
    };
  },

  mounted() {
    this.$store.commit("setWizardTitle", `Quote Slip Processing`);
    this.initWizard();

    // this.startPageLoading();
    //
    // // Extract information from URL params
    // const queryParams = this.$route.query;
    //
    // this.stopPageLoading();
  },

  computed: {
  },

  methods: {
    searchAbn: function() {
      if(!this.abnSearchString) {
        return;
      }
      this.abnEntity = null;
      this.abnSearchError = null;

      this.searchingAbn = true;
      const searchString = this.abnSearchString.replace(/\s/g, '');
      ApiService.get(`/prospect-form/abn-search`,
          { params: {searchString: searchString} }
      )
          .then(({ data }) => {
            const entity = data.result;

            if(!entity || !entity.abn || !entity.entityName) {
              this.abnSearchError = 'No entity found for the given ABN';
            }
            else if(entity.abnStatus !== 'Active') {
              this.abnSearchError = 'This ABN is not in active status';
            }
            else {
              this.abnEntity = entity;
            }
            this.searchingAbn = false;
          })
          .catch((error) => {
            this.searchingAbn = false;
            this.abnSearchError = 'Error while searching ABN - please try again';
            if(process.env.NODE_ENV === 'development') {
              console.log(error);
            }
          });
    },

    uploadFiles: async function() {
      this.clearError();

      const isFormValid = await this.$refs.quoteSlipForm.validate();
      if(!isFormValid) {
        return;
      }

      this.startPageLoading();

      const formData = new FormData();
      for (const [i, file] of this.selectedFiles.entries()) {
        formData.append(`quoteSlipFiles[${i}]`, file);
      }

      const config = {
        headers: { "Content-Type": "multipart/form-data" },
        params: { brand: this.brand }
      };

      ApiService.post(`/quote-slip-processing/upload`, formData, config)
          .then(({ data }) => {
            if(data && data.result) {
              this.jobId = data?.result?.workflow_id;
              this.jobStatus = data?.result?.status;
              console.log(`Job ID = ${this.jobId}`);
            }
            else {
              this.setError(`Something went wrong! Please try again.`);
            }
            this.stopPageLoading();

            if(this.jobId) {
              this.jobProcessing = true;

              // Start checking for job status updates
              setTimeout(this.checkJobStatus, this.refreshInterval);
            }
          })
          .catch((error) => {
            this.jobProcessing = false;
            this.handleError(error);
          });
    },

    checkJobStatus: function() {
      if(this.jobStatus === 'running' || this.jobStatus === 'accepted') {
        ApiService.get(`/quote-slip-processing/job-status/${this.jobId}`)
            .then(({ data }) => {
              const result = data?.result;
              this.jobStatus = result?.status;
              this.jobPercent = result?.progress?.percentage || null;

              setTimeout(this.checkJobStatus, this.refreshInterval);
            })
            .catch((error) => {
              this.jobProcessing = false;
              this.jobPercent = null;
              this.handleError(error);
            });
      }
      else {
        if(this.jobStatus === 'completed') {
          ApiService.get(`/quote-slip-processing/job-result/${this.jobId}`)
              .then(({ data }) => {
                this.extractedData = data?.result;
                if(!this.extractedData) {
                  this.setError(`No data extracted from the uploaded files. Please try again.`);
                  return;
                }

                const abn = this.extractedData?.insuredEntity?.ABN;
                if(abn) {
                  this.abnSearchString = abn;
                  this.searchAbn();
                }
                this.jobProcessing = false;
                this.jobPercent = null;
              })
              .catch((error) => {
                this.jobProcessing = false;
                this.jobPercent = null;
                this.handleError(error);
              });
        }
        else {
          this.jobProcessing = false;
          this.jobPercent = null;
          this.setError(`Something went wrong! Please try again.`);
        }
      }
    },

    showViewExtractedDataModal: function () {
      this.$refs["viewDataModal"].show();
    },

    createProposalForm: async function () {
      this.clearError();

      const isFormValid = await this.$refs.quoteSlipForm.validate();
      if(!isFormValid) {
        return;
      }

      this.startPageLoading();

      const payload = {
        jobId: this.jobId,
        brand: this.brand,
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        abnEntity: this.abnEntity
      }

      ApiService.post(`/quote-slip-processing/proposal-form`, payload)
          .then(({ data }) => {
            const proposalFormLink = data.result;
            if(proposalFormLink) {
              window.open(proposalFormLink);
              this.resetAll();
            }
            else {
              this.setError(`Could not generate a proposal form!`);
            }

            this.stopPageLoading();
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    resetAll: function () {
      this.brand = 'Fuse';
      this.firstName = null;
      this.lastName = null;
      this.email = null;
      this.selectedFiles = null;
      this.jobId = null;
      this.jobStatus = null;
      this.jobProcessing = false;
      this.jobPercent = null;
      this.extractedData = null;
      this.$refs.quoteSlipForm.reset();
    },

    initWizard: function () {
      // Initialize form wizard
      const wizard = new KTWizard("quoteSlipFormWizard", {
        startStep: 1, // Initial active step number
        clickableSteps: false, // Do not allow step clicking
      });

      this.wizardObj = wizard;

      // Change event - Scroll to top of the page
      wizard.on("change", (/*wizardObj*/) => {
        setTimeout(function () {
          KTUtil.scrollTop();
        }, 500);
      });
    },

    setError: function (message) {
      KTUtil.scrollTop();
      this.errorAlert = message;
    },

    clearError: function () {
      this.errorAlert = null;
    },

    handleError: function(errorObj) {
      this.stopPageLoading();
      let errorMsg = null;

      if(errorObj) {
        let responseStatus = _.get(errorObj, "response.status");
        let errorMessage = errorObj.message || '';
        if (responseStatus === 401 || errorMessage.includes("status code 401") ||
            responseStatus === 403 || errorMessage.includes("status code 403")
        ) {
          errorMsg = "This session is invalid or expired. Please close this window.";
        }
        else {
          let responseResult = _.get(errorObj, "response.data.result");
          if (_.isString(responseResult)) {
            let applicationErrorPrefix = "UI_ERROR:";

            if (responseResult.startsWith(applicationErrorPrefix)) {
              errorMsg = responseResult.substring(applicationErrorPrefix.length, responseResult.length);
            }
          }
        }
      }
      if (!errorMsg) {
        console.log(errorObj);
        errorMsg = "An unexpected error has occurred.";
      }

      this.setError(errorMsg);
      KTUtil.scrollTop();
    },

    startPageLoading: function() {
      this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
    },

    stopPageLoading: function() {
      this.$store.dispatch(REMOVE_BODY_CLASSNAME, "page-loading");
    }
  },
};
</script>
